import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Building, Edit, Power, Trash2, Search, Users, Calendar, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import LocationSearch from '@/components/LocationSearch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManagePGs = () => {
  const { token, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [pgs, setPgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showCyclesModal, setShowCyclesModal] = useState(false);
  const [selectedPG, setSelectedPG] = useState(null);
  const [members, setMembers] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    owner_name: '',
    owner_phone: '',
    address: '',
    state: '',
    latitude: null,
    longitude: null,
    cycle_start_date: '',
    status: 'active'
  });

  useEffect(() => {
    if (!isSuperAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchPGs();
  }, []);

  const fetchPGs = async () => {
    try {
      const response = await axios.get(`${API}/admin/pgs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPgs(response.data);
    } catch (error) {
      toast.error('Failed to fetch PGs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePG = async () => {
    try {
      await axios.post(`${API}/admin/pgs`, {
        name: formData.name,
        owner_name: formData.owner_name,
        owner_phone: formData.owner_phone,
        address: formData.address,
        state: formData.state,
        latitude: formData.latitude,
        longitude: formData.longitude,
        cycle_start_date: new Date(formData.cycle_start_date).toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('PG created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchPGs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create PG');
    }
  };

  const handleUpdatePG = async () => {
    try {
      const updateData = {
        name: formData.name,
        owner_name: formData.owner_name,
        owner_phone: formData.owner_phone,
        address: formData.address,
        state: formData.state,
        latitude: formData.latitude,
        longitude: formData.longitude,
        status: formData.status
      };
      if (formData.cycle_start_date) {
        updateData.cycle_start_date = new Date(formData.cycle_start_date).toISOString();
      }
      
      await axios.put(`${API}/admin/pgs/${selectedPG.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('PG updated successfully');
      setShowEditModal(false);
      resetForm();
      fetchPGs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update PG');
    }
  };

  const handleToggleStatus = async (pg) => {
    const newStatus = pg.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.put(`${API}/admin/pgs/${pg.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`PG ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      fetchPGs();
    } catch (error) {
      toast.error('Failed to update PG status');
    }
  };

  const handleDeletePG = async (pgId) => {
    if (!window.confirm('Are you sure you want to delete this PG? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/admin/pgs/${pgId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('PG deleted successfully');
      fetchPGs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete PG');
    }
  };

  const fetchMembers = async (pg) => {
    try {
      const response = await axios.get(`${API}/admin/pgs/${pg.id}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data.members);
      setSelectedPG(pg);
      setShowMembersModal(true);
    } catch (error) {
      toast.error('Failed to fetch members');
    }
  };

  const fetchCycles = async (pg) => {
    try {
      const response = await axios.get(`${API}/admin/pgs/${pg.id}/cycles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCycles(response.data.cycles || []);
      setSelectedPG(pg);
      setShowCyclesModal(true);
    } catch (error) {
      toast.error('Failed to fetch cycles');
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this member?')) return;
    
    try {
      await axios.delete(`${API}/admin/pgs/${selectedPG.id}/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member deleted successfully');
      fetchMembers(selectedPG);
    } catch (error) {
      toast.error('Failed to delete member');
    }
  };

  const openEditModal = (pg) => {
    setSelectedPG(pg);
    setFormData({
      name: pg.name,
      owner_name: pg.owner_name,
      owner_phone: pg.owner_phone,
      address: pg.address,
      state: pg.state,
      latitude: pg.latitude || null,
      longitude: pg.longitude || null,
      cycle_start_date: pg.cycle_start_date ? pg.cycle_start_date.split('T')[0] : '',
      status: pg.status
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      owner_name: '',
      owner_phone: '',
      address: '',
      state: '',
      latitude: null,
      longitude: null,
      cycle_start_date: '',
      status: 'active'
    });
    setSelectedPG(null);
  };

  const filteredPGs = pgs.filter(pg =>
    pg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pg.owner_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pg.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <Input
            placeholder="Search PGs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-pgs-input"
          />
        </div>
        <Button
          className="bg-indigo-600 hover:bg-indigo-700"
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          data-testid="create-pg-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add PG / Co-Living
        </Button>
      </div>

      {/* PGs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPGs.map((pg) => (
          <Card key={pg.id} className="card-hover" data-testid={`pg-card-${pg.id}`}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${pg.status === 'active' ? 'bg-green-50' : 'bg-red-50'}`}>
                    <Building className={`w-5 h-5 ${pg.status === 'active' ? 'text-green-600' : 'text-red-600'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{pg.name}</h3>
                    <Badge className={pg.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {pg.status}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <Users className="w-4 h-4" />
                  <span>Owner: {pg.owner_name}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Phone className="w-4 h-4" />
                  <span>{pg.owner_phone}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <MapPin className="w-4 h-4" />
                  <span>{pg.address}, {pg.state}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Calendar className="w-4 h-4" />
                  <span>Cycle Start: {format(new Date(pg.cycle_start_date), 'EEE, dd MMM yyyy')}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-4 mt-4 border-t border-slate-100">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchMembers(pg)}
                  data-testid={`view-members-${pg.id}`}
                >
                  <Users className="w-4 h-4 mr-1" />
                  Members
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchCycles(pg)}
                  data-testid={`view-cycles-${pg.id}`}
                >
                  <Calendar className="w-4 h-4 mr-1" />
                  Cycles
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditModal(pg)}
                  data-testid={`edit-pg-${pg.id}`}
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleToggleStatus(pg)}
                  className={pg.status === 'active' ? 'text-amber-600' : 'text-green-600'}
                  data-testid={`toggle-pg-${pg.id}`}
                >
                  <Power className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeletePG(pg.id)}
                  className="text-red-600"
                  data-testid={`delete-pg-${pg.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredPGs.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg">No PGs found</p>
            <p className="text-slate-400 text-sm">Create your first PG to get started</p>
          </CardContent>
        </Card>
      )}

      {/* Create PG Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New PG / Co-Living Space</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">PG Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter PG name"
                data-testid="pg-name-input"
              />
            </div>
            <div>
              <Label htmlFor="owner_name">Owner Name *</Label>
              <Input
                id="owner_name"
                value={formData.owner_name}
                onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                placeholder="Enter owner name"
              />
            </div>
            <div>
              <Label htmlFor="owner_phone">Owner Phone *</Label>
              <Input
                id="owner_phone"
                value={formData.owner_phone}
                onChange={(e) => setFormData({ ...formData, owner_phone: e.target.value })}
                placeholder="Enter phone number"
              />
            </div>
            <div>
              <Label htmlFor="address">Address *</Label>
              <LocationSearch
                value={formData.address}
                onChange={(val) => setFormData({ ...formData, address: val })}
                onSelect={(loc) => setFormData({ ...formData, address: loc.address, latitude: loc.latitude, longitude: loc.longitude })}
              />
            </div>
            <div>
              <Label htmlFor="state">State *</Label>
              <Input
                id="state"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                placeholder="Enter state"
              />
            </div>
            <div>
              <Label htmlFor="cycle_start_date">Laundry Cycle Start Date *</Label>
              <Input
                id="cycle_start_date"
                type="date"
                value={formData.cycle_start_date}
                onChange={(e) => setFormData({ ...formData, cycle_start_date: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1">First pickup day. Cycle repeats every Tue/Fri.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700" 
              onClick={handleCreatePG}
              disabled={!formData.name || !formData.owner_name || !formData.cycle_start_date}
            >
              Create PG
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit PG Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit PG</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-name">PG Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-owner_name">Owner Name *</Label>
              <Input
                id="edit-owner_name"
                value={formData.owner_name}
                onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-owner_phone">Owner Phone *</Label>
              <Input
                id="edit-owner_phone"
                value={formData.owner_phone}
                onChange={(e) => setFormData({ ...formData, owner_phone: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-address">Address *</Label>
              <LocationSearch
                value={formData.address}
                onChange={(val) => setFormData({ ...formData, address: val })}
                onSelect={(loc) => setFormData({ ...formData, address: loc.address, latitude: loc.latitude, longitude: loc.longitude })}
              />
            </div>
            <div>
              <Label htmlFor="edit-state">State *</Label>
              <Input
                id="edit-state"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-status">Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleUpdatePG}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Members Modal */}
      <Dialog open={showMembersModal} onOpenChange={setShowMembersModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Members - {selectedPG?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {members.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No members registered yet</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {members.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">{member.full_name}</p>
                      <p className="text-sm text-slate-500">{member.phone}</p>
                      <p className="text-xs text-slate-400">{member.address}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteMember(member.id)}
                      className="text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cycles Modal */}
      <Dialog open={showCyclesModal} onOpenChange={setShowCyclesModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Laundry Cycles - {selectedPG?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-xs text-slate-500 mb-4">
              Pickup and delivery schedule based on the cycle start date.
            </p>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {cycles.map((cycle, idx) => {
                const pickupDate = cycle.pickup_date ? new Date(cycle.pickup_date) : null;
                const deliveryDate = cycle.delivery_date ? new Date(cycle.delivery_date) : null;
                const now = new Date();
                const isPast = pickupDate && pickupDate < now;
                const isCurrent = pickupDate && !isPast && idx === cycles.findIndex(c => new Date(c.pickup_date) >= now);

                return (
                  <div
                    key={idx}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      isCurrent ? 'border-indigo-300 bg-indigo-50' : isPast ? 'border-slate-100 bg-slate-50 opacity-60' : 'border-slate-200'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      isCurrent ? 'bg-indigo-600 text-white' : isPast ? 'bg-slate-300 text-white' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {cycle.cycle_number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-4 text-sm">
                        <span className="font-medium text-slate-700">
                          {pickupDate ? format(pickupDate, 'EEE, dd MMM yyyy') : '-'}
                        </span>
                        {isCurrent && <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">Next</Badge>}
                      </div>
                      <div className="flex gap-4 text-xs text-slate-500 mt-0.5">
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
                          Pickup
                        </span>
                        {deliveryDate && (
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                            Delivery
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagePGs;
